import { Card } from "~/components/ui/card";
import "animate.css";
import { useState, useMemo } from "react";
import { cn } from "~/lib/utils";
import { Link } from "@remix-run/react";
import { areaSlug } from "~/lib/helpers/slugHelper";
import { getRegionUrlPrefixWithCountryCode } from "~/lib/helpers/slugHelper";
import { OhButton } from "~/components/ui/custom-radix-components/button";

interface Location {
  id: string;
  state: string;
  stateimage: string;
  cities: any[];
  totalCitiesCount?: number;
}

interface LocationCardProps {
  location: Location;
  region: string;
  className?: string;
}

const MAX_CITIES_DISPLAYED = 12; // Limit initial cities to reduce DOM size

export default function LocationCard({
  location,
  className,
  region,
}: LocationCardProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [showAllCities, setShowAllCities] = useState(false);

  // Limit cities rendered to reduce DOM nodes - only show all when expanded
  const displayedCities = useMemo(() => {
    if (!location?.cities || !Array.isArray(location.cities)) return [];
    if (showAllCities || location.cities.length <= MAX_CITIES_DISPLAYED) {
      return location.cities;
    }
    return location.cities.slice(0, MAX_CITIES_DISPLAYED);
  }, [location?.cities, showAllCities]);

  const hasMoreCities = (location?.totalCitiesCount ?? location?.cities?.length) > displayedCities.length;
  const moreCount = (location?.totalCitiesCount ?? location?.cities?.length) - displayedCities.length;

  return (
    <Card
      className={cn(`relative h-full w-full overflow-hidden group rounded-lg shadow-custom bg-black `, className)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        setIsHovered(false);
        setShowAllCities(false); // Reset when mouse leaves
      }}
    >
      <img
        src={location?.stateimage || "/placeholder.svg"}
        alt={location?.state}
        title={`${location?.state}, ${location.cities[0].country}`}
        width={400}
        height={530}
        className='absolute inset-0 object-cover w-full h-full'
        loading="lazy"
      />
      <div className='absolute bottom-4 left-4 right-4'>
        <h3
          className={`text-white text-xl font-bold ${isHovered ? "animate__animated animate__fadeInUp" : "opacity-100"}`}
        >
          {location?.state}
        </h3>
        {isHovered && (
          <div
            className={`mt-4 gap-2 text-md max-h-[400px] overflow-y-auto text-white ${isHovered ? "animate__animated animate__fadeInUp" : "opacity-0"}`}
          >
            {displayedCities.map((city) => {
              const areaLink = areaSlug({
                webPrimaryKeyword: city?.webPrimaryKeyword,
                state: city?.state,
                country: city?.country,
                id: city?.sfid,
                suburb: city?.suburb,
                city: city?.city,
              });

              return (
                <Link key={city?.city || city?.sfid} to={areaLink} target="_blank">
                  <OhButton
                    className='h-auto m-1 py-1 px-2 bg-white hover:bg-red-500 hover:text-white text-black font-small text-sm whitespace-nowrap'
                  >
                    {city?.city}
                  </OhButton>
                </Link>
              );
            })}
            {hasMoreCities && !showAllCities && (
              <Link to={`${getRegionUrlPrefixWithCountryCode(region)}/areas`}>
                <OhButton className="h-auto m-1 py-1 px-2 bg-oh-primary hover:bg-oh-primary/90 text-white font-small text-sm whitespace-nowrap">
                  +{moreCount} more
                </OhButton>
              </Link>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}
